<script setup lang="ts">
import { useElementSize } from "@vueuse/core";
import { computed, ref } from "vue";

export type TileHeight = "small" | "medium" | "large";

export interface Tile {
    key: string | number;
    name: string;
    thumbnail?: string;
    height: TileHeight;
    /** Extra CSS class on the tile, e.g. "is-new" */
    class?: string;
}

const props = defineProps<{ tiles: Tile[] }>();
const emit = defineEmits<{ select: [tile: Tile] }>();

// Pixel heights of the tile size classes - must match .tile-list-item in styles/less/elements.less
const tileHeights: Record<TileHeight, number> = { small: 152, medium: 198, large: 237 };
const gutter = 20;

const list = ref<HTMLElement>();
const { width } = useElementSize(list);

/**
 * Masonry layout (replaces the old freewall plugin): 1 column below 600px, 2 below 1000px, otherwise 3.
 * Each tile goes into the currently shortest column (leftmost on a tie).
 */
const layout = computed(() => {
    const columns = width.value >= 1000 ? 3 : width.value >= 600 ? 2 : 1;
    // Whole pixels, like freewall did
    const columnWidth = Math.round((width.value - gutter * (columns - 1)) / columns);
    const columnBottoms = new Array(columns).fill(0);

    const positions = props.tiles.map(tile => {
        const column = columnBottoms.indexOf(Math.min(...columnBottoms));
        const top = columnBottoms[column];
        columnBottoms[column] += tileHeights[tile.height] + gutter;
        return { left: column * (columnWidth + gutter), top, width: columnWidth };
    });

    return { positions, height: Math.max(0, Math.max(...columnBottoms) - gutter) };
});
</script>

<template>
    <ul ref="list" :style="{ position: 'relative', height: layout.height + 'px' }">
        <li v-for="(tile, i) in tiles" :key="tile.key"
            class="tile-list-item is-shown" :class="[tile.height, tile.class]"
            :style="{ position: 'absolute', left: layout.positions[i].left + 'px', top: layout.positions[i].top + 'px', width: layout.positions[i].width + 'px' }"
            @click="emit('select', tile)">
            <div v-if="tile.thumbnail" class="tile-img" :style="{ backgroundImage: `url('${tile.thumbnail}')` }"></div>
            <div class="tile-name">
                <span v-html="tile.name"></span>
            </div>
        </li>
    </ul>
</template>
